import React, { useCallback } from 'react'
import moment from 'moment'
import { isNil, noop } from 'lodash-es'
import { AuditDetail, NegotiationDetail, NegotiationStatus, NegotiationType, TradeConditionFlag, UserPreference } from 'api'
import { CollapseContentWrapper, DeltaIconContainer, SummaryCollapsePanel, TicketFields } from 'view/shared/ticket'
import { CollapsePanel } from 'view/components/panels'
import { DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import {
  AxeTradeConditionButtonsField,
  CounterpartySponsorComboboxField,
  DecisionMakerSelectboxField,
  MasterLegalAgreementTypeSelectboxField,
  OTWSelectboxField,
  TermSelectboxField,
  TradingDatePickerField
} from 'view/shared/entity'
import { NegotiationFieldNames } from '../LiveNegotiationsGrid/LiveGridColumnNames'
import {
  canUpdateAttributes,
  getNegotiatedValue,
  getNegotiationModificationColor,
  isAuditDetail,
  isClearedNegotiation,
  isNegotiationHistory,
  shouldStartEdit
} from '../shared/utils'
import {
  getAxeTradeConditionModificationType,
  getBondPriceModificationType,
  getEndDateDelta,
  getEndDateModificationType,
  getHaircutDelta,
  getHaircutModificationType,
  getNotionalDelta,
  getNotionalModificationType,
  getRateDelta,
  getRateModificationType,
  getStartConsiderationDelta,
  getStartDateDelta,
  getStartDateModificationType,
  getTermModificationType
} from '../shared/deltaGetters'
import {
  formatRepoType,
  getExecutingTradingEntityName,
  getFormattedDirection,
  getHaircutFormatOptions,
  getIsin,
  getNegotiationDetailBondPriceEditable,
  getNegotiationDetailNotionalEditable,
  getNegotiationDetailStartConsiderationEditable,
  getNotionalFormatOptions,
  getOrganisationField,
  getRateFormatOptions,
  getSettlementVenueName,
  getSponsorName,
  getStartConsiderationFormatOptions,
  getTradingEntityField,
  getTradingEntityLei,
  getTradingEntityName,
  isNegotiationDetailAuditDetailNonClearedNonGC
} from 'view/shared/datagrid'
import { getFormattedTime, getTimeLeft } from 'utils'
import { getDateEditorFormat, ReferenceRateHelper, RepoClassificationHelper, UserHelper } from 'model'
import { Locale, Translation } from 'locale'
import { deltaIcon } from 'view/components/datagrid/styles/gridIconSet'
import * as constants from 'view/styles/constants'
import { CallableTypeSelectboxField } from 'view/shared/entity/CallableTypeSelectbox'

interface RFQTicketFieldsProps {
  negotiationDetail: NegotiationDetail | AuditDetail
  userPreference: UserPreference
  onChangeField?: () => void
  onUpdateField: (value: any, field?: string, rawValue?: any) => unknown
}

export const RFQTicketFields: React.FunctionComponent<RFQTicketFieldsProps> = props => {
  const { negotiationDetail, userPreference, onChangeField, onUpdateField } = props
  const { collateral, negotiation, product, productSector, referenceRate, ownOrder } = negotiationDetail

  const editable =
    shouldStartEdit(negotiationDetail) &&
    canUpdateAttributes(negotiationDetail) &&
    negotiationDetail.negotiation.negotiationType !== NegotiationType.CONTINGENT_SUMMARY
  const decisionMakersEditable =
    shouldStartEdit(negotiationDetail) && negotiationDetail.negotiation.negotiationType !== NegotiationType.CONTINGENT_SUMMARY
  const cleared = isClearedNegotiation(negotiationDetail)
  const notionalFormatOptions = getNotionalFormatOptions(userPreference.amountFormat)
  const dateEditorFormat = getDateEditorFormat(userPreference.dateFormat)
  const currency = negotiationDetail.negotiation.currency
  const negotiatedStartDate = isNegotiationHistory(negotiation) ? negotiation.startDate : getNegotiatedValue(negotiation.startDate)
  const negotiatedEndDate = isNegotiationHistory(negotiation) ? negotiation.endDate : getNegotiatedValue(negotiation.endDate)
  const rfqDuration = isNegotiationHistory(negotiation)
    ? null
    : moment.duration(moment(negotiation.rfqEndTime).diff(moment(negotiation.rfqStartTime))).asMilliseconds()
  const isQuoted = negotiation.status === NegotiationStatus.QUOTED
  const otwEndTime = isNegotiationHistory(negotiation) ? negotiation.startDate : negotiation.otwEndTime || negotiation.rfqEndTime
  const quotedOTWDuration =
    !isNegotiationHistory(negotiation) && isQuoted && negotiation.otwStartTime
      ? moment.duration(moment(otwEndTime).diff(moment(negotiation.otwStartTime))).asMilliseconds()
      : null
  const remainingRFQTimeMins = isNegotiationHistory(negotiation)
    ? undefined
    : moment.duration(getTimeLeft(negotiation.rfqEndTime)).asMinutes()
  const sponsorName = getSponsorName(negotiationDetail, true)
  const counterpartyValue = isNegotiationHistory(negotiation)
    ? negotiation.counterpartyId
    : getNegotiatedValue(negotiation.counterpartyIdNegotiableField)
  const book = isNegotiationHistory(negotiation)
    ? ownOrder
      ? negotiation.initiatorBook
      : negotiation.responderBook
    : getNegotiatedValue(ownOrder ? negotiation.initiatorBook : negotiation.responderBook)
  const strategy = isNegotiationHistory(negotiation)
    ? ownOrder
      ? negotiation.initiatorStrategy
      : negotiation.responderStrategy
    : getNegotiatedValue(ownOrder ? negotiation.initiatorStrategy : negotiation.responderStrategy)

  const renderSponsorField = () =>
    ownOrder ? (
      <TextInputField label={Locale.translate(Translation.TC_SPONSORING_BANK)} readOnly={true} onUpdate={noop} value={sponsorName} />
    ) : counterpartyValue ? (
      <CounterpartySponsorComboboxField
        label={Locale.translate(Translation.TC_SPONSORING_BANK)}
        fieldName={NegotiationFieldNames.COUNTERPARTY_SPONSOR_ID_MODIFIED}
        validationField={NegotiationFieldNames.COUNTERPARTY_SPONSOR_ID}
        validationIssues={negotiationDetail.validationIssues}
        counterpartyId={counterpartyValue}
        clearingHouseId={negotiation.clearingHouseId}
        productSectorId={productSector?.id}
        readOnly={!editable}
        onUpdate={onUpdateField}
        value={
          isNegotiationHistory(negotiation)
            ? negotiation.counterpartySecondaryCoverUserId
            : getNegotiatedValue(negotiation.counterpartySponsorId)
        }
      />
    ) : null

  const onUpdateAxeTradeCondition = useCallback(
    (isAxed: boolean, tradeConditionFlag: TradeConditionFlag) => {
      const axed = isNegotiationHistory(negotiation) ? negotiation.axed : getNegotiatedValue(negotiation.isAxed)
      const tradeCondition = isNegotiationHistory(negotiation)
        ? negotiation.tradeConditionFlag
        : getNegotiatedValue(negotiation.tradeConditionFlag)
      if (axed !== isAxed) {
        onUpdateField(isAxed, NegotiationFieldNames.AXE)
      }
      if (tradeCondition !== tradeConditionFlag) {
        onUpdateField(tradeConditionFlag, NegotiationFieldNames.ROLL_UNWIND)
      }
    },
    [onUpdateField, negotiation]
  )

  const createDeltaLabel = useCallback(
    (labelText: Translation, params?: any) => (
      <DeltaIconContainer>
        {Locale.translate(labelText, params)}&nbsp;{deltaIcon}
      </DeltaIconContainer>
    ),
    []
  )

  const partyCollapsePanelHasValidationIssues = negotiationDetail.validationIssues.some(issue =>
    ['counterpartySponsorId', 'responderInvestmentDecisionMaker', 'responderExecutionDecisionMaker'].includes(issue.fieldName)
  )

  return (
    <TicketFields>
      <SummaryCollapsePanel
        dataTestId={'SummaryCollapsePanel-RFQTicketFields-TicketQuickSummary'}
        title={Locale.translate(Translation.TC_TICKET_QUICK_SUMMARY)}
        defaultCollapsed={false}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                fieldName={NegotiationFieldNames.NOTIONAL_MODIFIED}
                validationField={NegotiationFieldNames.NOTIONAL}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getNotionalModificationType(negotiationDetail.negotiation.notional),
                        negotiationDetail
                      )
                }
                readOnly={!editable || !getNegotiationDetailNotionalEditable(negotiationDetail)}
                min={collateral?.ticketIncrement}
                step={collateral?.ticketIncrement}
                formatOptions={notionalFormatOptions}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={isNegotiationHistory(negotiation) ? negotiation.notional : getNegotiatedValue(negotiation.notional)}
                dataTestId={'RFQTicketFields-NumericInputField-Notional'}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_RATE_VS_REFERENCE_RATE, {
                  referenceRate: referenceRate ? ReferenceRateHelper.getName(referenceRate) : null
                })}
                fieldName={NegotiationFieldNames.RATE_MODIFIED}
                validationField={NegotiationFieldNames.RATE}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(() => getRateModificationType(negotiationDetail.negotiation.rate), negotiationDetail)
                }
                readOnly={!editable}
                formatOptions={getRateFormatOptions(referenceRate)}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={isNegotiationHistory(negotiation) ? negotiation.rate : getNegotiatedValue(negotiation.rate)}
                dataTestId={'RFQTicketFields-NumericInputField-Rate'}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_HAIRCUT)}
                fieldName={NegotiationFieldNames.HAIRCUT_MODIFIED}
                validationField={NegotiationFieldNames.HAIRCUT}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getHaircutModificationType(negotiationDetail.negotiation.haircut),
                        negotiationDetail
                      )
                }
                readOnly={!editable || !isNegotiationDetailAuditDetailNonClearedNonGC(negotiationDetail)}
                min={0}
                formatOptions={getHaircutFormatOptions(undefined, userPreference.haircutSign, ownOrder, negotiation.direction)}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={isNegotiationHistory(negotiation) ? negotiation.haircut : getNegotiatedValue(negotiation.haircut)}
                dataTestId={'RFQTicketFields-NumericInputField-HairCut'}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_START_DATE)}
                fieldName={NegotiationFieldNames.START_DATE_MODIFIED}
                validationField={NegotiationFieldNames.START_DATE}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getStartDateModificationType(negotiationDetail.negotiation.startDate),
                        negotiationDetail
                      )
                }
                readOnly={!editable}
                dateFormat={dateEditorFormat}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={negotiatedStartDate}
                dataTestId={'RFQTicketFields-TradingDatePickerField-StartDate'}
              />
            </DialogField>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_END_DATE)}
                fieldName={NegotiationFieldNames.END_DATE_MODIFIED}
                validationField={NegotiationFieldNames.END_DATE}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getEndDateModificationType(negotiationDetail.negotiation.endDate),
                        negotiationDetail
                      )
                }
                readOnly={!editable}
                dateFormat={dateEditorFormat}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={negotiatedEndDate}
                dataTestId={'RFQTicketFields-TradingDatePickerField-EndDate'}
              />
            </DialogField>
            <DialogField>
              <TermSelectboxField
                label={Locale.translate(Translation.TC_TERM)}
                fieldName={NegotiationFieldNames.TERM_MODIFIED}
                validationField={NegotiationFieldNames.TERM}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(() => getTermModificationType(negotiationDetail.negotiation.term), negotiationDetail)
                }
                readOnly={!editable}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={isNegotiationHistory(negotiation) ? negotiation.term : getNegotiatedValue(negotiation.term)}
                dataTestId={'RFQTicketFields-TermSelectboxField-Term'}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COLLATERAL_PRICE)}
                fieldName={NegotiationFieldNames.BOND_PRICE_MODIFIED}
                validationField={NegotiationFieldNames.BOND_PRICE}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getBondPriceModificationType(negotiationDetail.negotiation.bondPrice),
                        negotiationDetail
                      )
                }
                readOnly={!editable || !getNegotiationDetailBondPriceEditable(negotiationDetail)}
                min={0}
                maxLength={constants.bondPriceMaxFieldLength}
                formatOptions={{
                  maxFractionDigits: constants.bondPriceMaxFractionDigits
                }}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={isNegotiationHistory(negotiation) ? negotiation.bondPrice : getNegotiatedValue(negotiation.bondPrice)}
                dataTestId={'RFQTicketFields-NumericInputField-CollateralPrice'}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_START_CONSIDERATION)}
                fieldName={NegotiationFieldNames.START_CONSIDERATION_MODIFIED}
                validationField={NegotiationFieldNames.START_CONSIDERATION}
                validationIssues={negotiationDetail.validationIssues}
                readOnly={!editable || !getNegotiationDetailStartConsiderationEditable(negotiationDetail)}
                formatOptions={getStartConsiderationFormatOptions()}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={
                  isNegotiationHistory(negotiation) ? negotiation.startConsideration : getNegotiatedValue(negotiation.startConsideration)
                }
              />
            </DialogField>
            <DialogField>
              <AxeTradeConditionButtonsField
                label={Locale.translate(Translation.TC_AXE_ROLL_UNWIND)}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(getAxeTradeConditionModificationType, negotiationDetail)
                }
                readOnly={!editable}
                onChange={onChangeField}
                onUpdate={onUpdateAxeTradeCondition}
                isAxed={isNegotiationHistory(negotiation) ? !!negotiation.axed : !!getNegotiatedValue(negotiation.isAxed)}
                tradeConditionFlag={
                  (isNegotiationHistory(negotiation)
                    ? negotiation.tradeConditionFlag
                    : getNegotiatedValue(negotiation.tradeConditionFlag)) ?? TradeConditionFlag.NONE
                }
              />
            </DialogField>
            <DialogField>
              {!ownOrder &&
                (isQuoted ? (
                  <TextInputField
                    label={Locale.translate(Translation.TC_OTW_TIME)}
                    readOnly={true}
                    onUpdate={noop}
                    value={isNil(quotedOTWDuration) ? null : getFormattedTime(quotedOTWDuration)}
                  />
                ) : (
                  <OTWSelectboxField
                    label={Locale.translate(Translation.TC_OTW_TIME)}
                    fieldName={NegotiationFieldNames.OTW_DURATION}
                    readOnly={!editable}
                    onChange={onChangeField}
                    onUpdate={onUpdateField}
                    remainingRFQTimeMins={remainingRFQTimeMins}
                    value={isAuditDetail(negotiationDetail) ? null : negotiationDetail.otwDuration}
                  />
                ))}
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_SETTLEMENT)}
                readOnly={true}
                onUpdate={noop}
                value={getSettlementVenueName(
                  isNegotiationHistory(negotiation)
                    ? negotiation.settlementVenue
                    : getNegotiatedValue(negotiation.settlementVenueNegotiable)
                )}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_COUNTERPARTY)}
                readOnly={true}
                onUpdate={noop}
                value={getTradingEntityName(negotiationDetail, false)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_TRADE_TYPE)}
                readOnly={true}
                onUpdate={noop}
                value={negotiation.repoClassification ? RepoClassificationHelper.getName(negotiation.repoClassification) : null}
                // value could be null - AoN summary
              />
            </DialogField>
            <DialogField>{cleared && renderSponsorField()}</DialogField>
          </DialogRow>
          {cleared && (
            <DialogRow>
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getExecutingTradingEntityName(negotiationDetail)}
                />
              </DialogField>
              <DialogField />
              <DialogField />
            </DialogRow>
          )}
        </CollapseContentWrapper>
      </SummaryCollapsePanel>

      <CollapsePanel dataTestId={'RFQTicketFields-CollapsePanel-TicketAmount'} title={Locale.translate(Translation.TC_TICKET_AMOUNT)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                fieldName={NegotiationFieldNames.NOTIONAL_MODIFIED}
                validationField={NegotiationFieldNames.NOTIONAL}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getNotionalModificationType(negotiationDetail.negotiation.notional),
                        negotiationDetail
                      )
                }
                readOnly={!editable || !getNegotiationDetailNotionalEditable(negotiationDetail)}
                min={collateral?.ticketIncrement}
                step={collateral?.ticketIncrement}
                formatOptions={notionalFormatOptions}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={isNegotiationHistory(negotiation) ? negotiation.notional : getNegotiatedValue(negotiation.notional)}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NOTIONAL_ORIGINAL, { amountFormat: userPreference.amountFormat })}
                readOnly={true}
                onUpdate={noop}
                formatOptions={notionalFormatOptions}
                value={isNegotiationHistory(negotiation) ? null : negotiation.notional.original}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={createDeltaLabel(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                readOnly={true}
                onUpdate={noop}
                formatOptions={getNotionalFormatOptions(userPreference.amountFormat, true)}
                value={isNegotiationHistory(negotiation) ? null : getNotionalDelta(negotiation.notional)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_DIRECTION)}
                readOnly={true}
                onUpdate={noop}
                value={getFormattedDirection(negotiation.direction, ownOrder, userPreference.directionFormat)}
              />
            </DialogField>
            <DialogField />
            <DialogField />
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel dataTestId={'RFQTicketFields-CollapsePanel-TicketPricing'} title={Locale.translate(Translation.TC_TICKET_PRICING)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_RATE_VS_REFERENCE_RATE, {
                  referenceRate: referenceRate ? ReferenceRateHelper.getName(referenceRate) : null
                })}
                fieldName={NegotiationFieldNames.RATE_MODIFIED}
                validationField={NegotiationFieldNames.RATE}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(() => getRateModificationType(negotiationDetail.negotiation.rate), negotiationDetail)
                }
                readOnly={!editable}
                formatOptions={getRateFormatOptions(referenceRate)}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={isNegotiationHistory(negotiation) ? negotiation.rate : getNegotiatedValue(negotiation.rate)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_REFERENCE_RATE)}
                readOnly={true}
                onUpdate={noop}
                value={referenceRate?.name}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={createDeltaLabel(Translation.TC_RATE)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={getRateFormatOptions(referenceRate, true)}
                value={isNegotiationHistory(negotiation) ? null : getRateDelta(negotiation.rate)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_RATE_TARGET)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={getRateFormatOptions(referenceRate)}
                value={isNegotiationHistory(negotiation) ? null : negotiation.rate.original}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_START_CONSIDERATION)}
                fieldName={NegotiationFieldNames.START_CONSIDERATION_MODIFIED}
                validationField={NegotiationFieldNames.START_CONSIDERATION}
                validationIssues={negotiationDetail.validationIssues}
                readOnly={!editable || !getNegotiationDetailStartConsiderationEditable(negotiationDetail)}
                formatOptions={getStartConsiderationFormatOptions()}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={
                  isNegotiationHistory(negotiation) ? negotiation.startConsideration : getNegotiatedValue(negotiation.startConsideration)
                }
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_START_CONSIDERATION_ORIGINAL)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={getStartConsiderationFormatOptions()}
                value={isNegotiationHistory(negotiation) ? null : negotiation.startConsideration.original}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={createDeltaLabel(Translation.TC_START_CONSIDERATION)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={getStartConsiderationFormatOptions(true)}
                value={isNegotiationHistory(negotiation) ? null : getStartConsiderationDelta(negotiation.startConsideration)}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_END_CONSIDERATION)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={{ minDp: 2 }}
                value={negotiation.endConsideration}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_HAIRCUT)}
                fieldName={NegotiationFieldNames.HAIRCUT_MODIFIED}
                validationField={NegotiationFieldNames.HAIRCUT}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getHaircutModificationType(negotiationDetail.negotiation.haircut),
                        negotiationDetail
                      )
                }
                readOnly={!editable || !isNegotiationDetailAuditDetailNonClearedNonGC(negotiationDetail)}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                min={0}
                formatOptions={getHaircutFormatOptions(undefined, userPreference.haircutSign, ownOrder, negotiation.direction)}
                value={isNegotiationHistory(negotiation) ? negotiation.haircut : getNegotiatedValue(negotiation.haircut)}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_HAIRCUT_TARGET)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={getHaircutFormatOptions(undefined, userPreference.haircutSign, ownOrder, negotiation.direction)}
                value={isNegotiationHistory(negotiation) ? null : negotiation.haircut.original}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={createDeltaLabel(Translation.TC_HAIRCUT)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={getHaircutFormatOptions(true, userPreference.haircutSign, ownOrder, negotiation.direction)}
                value={isNegotiationHistory(negotiation) ? null : getHaircutDelta(negotiation.haircut)}
              />
            </DialogField>
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel dataTestId={'RFQTicketFields-CollapsePanel-TicketTerm'} title={Locale.translate(Translation.TC_TICKET_TERM)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_START_DATE)}
                fieldName={NegotiationFieldNames.START_DATE_MODIFIED}
                validationField={NegotiationFieldNames.START_DATE}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getStartDateModificationType(negotiationDetail.negotiation.startDate),
                        negotiationDetail
                      )
                }
                readOnly={!editable}
                dateFormat={dateEditorFormat}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={negotiatedStartDate}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_START_DATE_TARGET)}
                readOnly={true}
                onUpdate={noop}
                value={
                  isNegotiationHistory(negotiation) ? null : Locale.formatDate(negotiation.startDate.original, userPreference.dateFormat)
                }
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={createDeltaLabel(Translation.TC_START_DATE)}
                readOnly={true}
                onUpdate={noop}
                value={isNegotiationHistory(negotiation) ? null : getStartDateDelta(negotiation.startDate)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_END_DATE)}
                fieldName={NegotiationFieldNames.END_DATE_MODIFIED}
                validationField={NegotiationFieldNames.END_DATE}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getEndDateModificationType(negotiationDetail.negotiation.endDate),
                        negotiationDetail
                      )
                }
                readOnly={!editable}
                dateFormat={dateEditorFormat}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={negotiatedEndDate}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_END_DATE_TARGET)}
                readOnly={true}
                onUpdate={noop}
                value={
                  isNegotiationHistory(negotiation) ? null : Locale.formatDate(negotiation.endDate.original, userPreference.dateFormat)
                }
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={createDeltaLabel(Translation.TC_END_DATE)}
                readOnly={true}
                onUpdate={noop}
                value={isNegotiationHistory(negotiation) ? null : getEndDateDelta(negotiation.endDate)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TermSelectboxField
                label={Locale.translate(Translation.TC_TERM)}
                fieldName={NegotiationFieldNames.TERM_MODIFIED}
                validationField={NegotiationFieldNames.TERM}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(() => getTermModificationType(negotiationDetail.negotiation.term), negotiationDetail)
                }
                readOnly={!editable}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={isNegotiationHistory(negotiation) ? negotiation.term : getNegotiatedValue(negotiation.term)}
              />
            </DialogField>
            <DialogField />
            <DialogField />
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel dataTestId={'RFQTicketFields-CollapsePanel-TicketTimers'} title={Locale.translate(Translation.TC_TICKET_TIMERS)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_RFQ_TIME)}
                readOnly={true}
                onUpdate={noop}
                value={isNil(rfqDuration) ? null : getFormattedTime(rfqDuration)}
              />
            </DialogField>
            <DialogField>
              {ownOrder || isQuoted ? (
                <TextInputField
                  label={Locale.translate(Translation.TC_OTW_TIME)}
                  readOnly={true}
                  onUpdate={noop}
                  value={isNil(quotedOTWDuration) ? null : getFormattedTime(quotedOTWDuration)}
                />
              ) : (
                <OTWSelectboxField
                  label={Locale.translate(Translation.TC_OTW_TIME)}
                  fieldName={NegotiationFieldNames.OTW_DURATION}
                  readOnly={!editable}
                  onChange={onChangeField}
                  onUpdate={onUpdateField}
                  remainingRFQTimeMins={remainingRFQTimeMins}
                  value={isAuditDetail(negotiationDetail) ? null : negotiationDetail.otwDuration}
                />
              )}
            </DialogField>
            <DialogField />
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel
        dataTestId={'RFQTicketFields-CollapsePanel-TicketInstrument'}
        title={Locale.translate(Translation.TC_TICKET_INSTRUMENT)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_ISIN_BUI)}
                readOnly={true}
                onUpdate={noop}
                value={getIsin(negotiationDetail)}
              />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_CUSIP)} readOnly={true} onUpdate={noop} value={collateral?.cusip} />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_ISSUER)} readOnly={true} onUpdate={noop} value={collateral?.issuer} />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_PRODUCT)} readOnly={true} onUpdate={noop} value={product?.name} />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_SECTOR)} readOnly={true} onUpdate={noop} value={productSector?.name} />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_REPO_TYPE)}
                readOnly={true}
                onUpdate={noop}
                value={formatRepoType(negotiation.repoType)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_CURRENCY)} readOnly={true} onUpdate={noop} value={currency} />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COLLATERAL_PRICE)}
                fieldName={NegotiationFieldNames.BOND_PRICE_MODIFIED}
                validationField={NegotiationFieldNames.BOND_PRICE}
                validationIssues={negotiationDetail.validationIssues}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(
                        () => getBondPriceModificationType(negotiationDetail.negotiation.bondPrice),
                        negotiationDetail
                      )
                }
                readOnly={!editable || !getNegotiationDetailBondPriceEditable(negotiationDetail)}
                min={0}
                maxLength={constants.bondPriceMaxFieldLength}
                formatOptions={{
                  maxFractionDigits: constants.bondPriceMaxFractionDigits
                }}
                onChange={onChangeField}
                onUpdate={onUpdateField}
                value={isNegotiationHistory(negotiation) ? negotiation.bondPrice : getNegotiatedValue(negotiation.bondPrice)}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COUPON)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={{ percent: true }}
                value={collateral?.coupon}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COLLATERAL_ACCRUED_INTEREST)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={{ minDp: 2 }}
                value={negotiation.accruedInterest}
              />
            </DialogField>
            <DialogField />
            <DialogField />
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel
        title={Locale.translate(Translation.TC_TICKET_PARTIES)}
        defaultCollapsed={!partyCollapsePanelHasValidationIssues}
        dataTestId={'RFQTicketFields-CollapsePanel-TicketParties'}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_COUNTERPARTY)}
                readOnly={true}
                onUpdate={noop}
                value={getTradingEntityName(negotiationDetail, false)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_COUNTERPARTY_LEI)}
                readOnly={true}
                onUpdate={noop}
                value={getTradingEntityLei(negotiationDetail, false)}
              />
            </DialogField>
            {cleared ? (
              <DialogField>{renderSponsorField()}</DialogField>
            ) : (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_COUNTERPARTY_BIC)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getTradingEntityField(negotiationDetail, 'bic', false)}
                />
              </DialogField>
            )}
          </DialogRow>
          <DialogRow>
            {cleared ? (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getExecutingTradingEntityName(negotiationDetail)}
                />
              </DialogField>
            ) : (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_COUNTERPARTY_FIRM)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getOrganisationField(negotiationDetail, 'legalName', false)}
                />
              </DialogField>
            )}
            {cleared ? (
              <DialogField />
            ) : (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_COUNTERPARTY_FIRM_LEI)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getOrganisationField(negotiationDetail, 'lei', false)}
                />
              </DialogField>
            )}
            <DialogField />
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MY_ENTITY)}
                readOnly={true}
                onUpdate={noop}
                value={getTradingEntityName(negotiationDetail, true)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MY_ENTITY_LEI)}
                readOnly={true}
                onUpdate={noop}
                value={getTradingEntityLei(negotiationDetail, true)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MY_ENTITY_BIC)}
                readOnly={true}
                onUpdate={noop}
                value={getTradingEntityField(negotiationDetail, 'bic', true)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MY_FIRM)}
                readOnly={true}
                onUpdate={noop}
                value={getOrganisationField(negotiationDetail, 'legalName', true)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MY_FIRM_LEI)}
                readOnly={true}
                onUpdate={noop}
                value={getOrganisationField(negotiationDetail, 'lei', true)}
              />
            </DialogField>
            <DialogField />
          </DialogRow>
          {ownOrder ? (
            <DialogRow>
              <DialogField>
                <DecisionMakerSelectboxField
                  label={Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER)}
                  fieldName={NegotiationFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_MODIFIED}
                  readOnly={!decisionMakersEditable}
                  onChange={onChangeField}
                  onUpdate={onUpdateField}
                  value={
                    isNegotiationHistory(negotiation)
                      ? negotiation.initiatorInvestmentDecisionMaker
                      : negotiation.initiatorInvestmentDecisionMaker && getNegotiatedValue(negotiation.initiatorInvestmentDecisionMaker)
                  }
                  dataTestId={'RFQTicketFields-InitiatorInvestmentDecisionMakerSelectboxField'}
                />
              </DialogField>
              <DialogField>
                <DecisionMakerSelectboxField
                  label={Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER)}
                  fieldName={NegotiationFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_MODIFIED}
                  readOnly={!decisionMakersEditable}
                  onChange={onChangeField}
                  onUpdate={onUpdateField}
                  value={
                    isNegotiationHistory(negotiation)
                      ? negotiation.initiatorExecutionDecisionMaker
                      : negotiation.initiatorExecutionDecisionMaker && getNegotiatedValue(negotiation.initiatorExecutionDecisionMaker)
                  }
                  dataTestId={'RFQTicketFields-InitiatorExecutionDecisionMakerSelectboxField'}
                />
              </DialogField>
              <DialogField>
                <MasterLegalAgreementTypeSelectboxField
                  label={Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENT)}
                  value={negotiation.masterLegalAgreementType}
                  readOnly={true}
                  onUpdate={noop}
                />
              </DialogField>
            </DialogRow>
          ) : (
            <DialogRow>
              <DialogField>
                <DecisionMakerSelectboxField
                  label={Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER)}
                  fieldName={NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_MODIFIED}
                  readOnly={!decisionMakersEditable}
                  onChange={onChangeField}
                  onUpdate={onUpdateField}
                  value={
                    isNegotiationHistory(negotiation)
                      ? negotiation.responderInvestmentDecisionMaker
                      : negotiation.responderInvestmentDecisionMaker && getNegotiatedValue(negotiation.responderInvestmentDecisionMaker)
                  }
                  validationIssues={negotiationDetail.validationIssues}
                  validationField={NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER}
                  dataTestId={'RFQTicketFields-ResponderInvestmentDecisionMakerSelectboxField'}
                />
              </DialogField>
              <DialogField>
                <DecisionMakerSelectboxField
                  label={Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER)}
                  fieldName={NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_MODIFIED}
                  readOnly={!decisionMakersEditable}
                  onChange={onChangeField}
                  onUpdate={onUpdateField}
                  value={
                    isNegotiationHistory(negotiation)
                      ? negotiation.responderExecutionDecisionMaker
                      : negotiation.responderExecutionDecisionMaker && getNegotiatedValue(negotiation.responderExecutionDecisionMaker)
                  }
                  validationIssues={negotiationDetail.validationIssues}
                  validationField={NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER}
                  dataTestId={'RFQTicketFields-ResponderExecutionDecisionMakerSelectboxField'}
                />
              </DialogField>
              <DialogField>
                <MasterLegalAgreementTypeSelectboxField
                  label={Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENT)}
                  value={negotiation.masterLegalAgreementType}
                  readOnly={true}
                  onUpdate={noop}
                />
              </DialogField>
            </DialogRow>
          )}
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_PRIMARY_SALES_COVER)}
                readOnly={true}
                onUpdate={noop}
                value={
                  ownOrder
                    ? negotiationDetail.fundPrimarySalesCoverUser && UserHelper.getName(negotiationDetail.fundPrimarySalesCoverUser)
                    : negotiationDetail.counterpartyPrimarySalesCoverUser &&
                      UserHelper.getName(negotiationDetail.counterpartyPrimarySalesCoverUser)
                }
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_SECONDARY_SALES_COVER)}
                readOnly={true}
                onUpdate={noop}
                value={
                  ownOrder
                    ? negotiationDetail.fundSecondarySalesCoverUser && UserHelper.getName(negotiationDetail.fundSecondarySalesCoverUser)
                    : negotiationDetail.counterpartySecondarySalesCoverUser &&
                      UserHelper.getName(negotiationDetail.counterpartySecondarySalesCoverUser)
                }
              />
            </DialogField>
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel dataTestId={'RFQTicketFields-CollapsePanel-TicketFlags'} title={Locale.translate(Translation.TC_TICKET_FLAGS)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <AxeTradeConditionButtonsField
                label={Locale.translate(Translation.TC_AXE_ROLL_UNWIND)}
                underline={
                  isAuditDetail(negotiationDetail)
                    ? null
                    : getNegotiationModificationColor(getAxeTradeConditionModificationType, negotiationDetail)
                }
                readOnly={!editable}
                onChange={onChangeField}
                onUpdate={onUpdateAxeTradeCondition}
                isAxed={isNegotiationHistory(negotiation) ? !!negotiation.axed : !!getNegotiatedValue(negotiation.isAxed)}
                tradeConditionFlag={
                  (isNegotiationHistory(negotiation)
                    ? negotiation.tradeConditionFlag
                    : getNegotiatedValue(negotiation.tradeConditionFlag)) ?? TradeConditionFlag.NONE
                }
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COMPETITION)}
                readOnly={true}
                onUpdate={noop}
                value={negotiation.competitionCount ?? 0}
              />
            </DialogField>
            <DialogField>
              <CallableTypeSelectboxField
                label={Locale.translate(Translation.TC_CALLABLE)}
                readOnly={true}
                onUpdate={noop}
                value={negotiation.callableType}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_CALL_DAYS)}
                readOnly={true}
                onUpdate={noop}
                value={negotiation.callablePeriodDays}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_SETTLEMENT)}
                readOnly={true}
                onUpdate={noop}
                value={getSettlementVenueName(
                  isNegotiationHistory(negotiation)
                    ? negotiation.settlementVenue
                    : getNegotiatedValue(negotiation.settlementVenueNegotiable)
                )}
              />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_CSD)} readOnly={true} onUpdate={noop} value={negotiation.csd} />
            </DialogField>
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel dataTestId={'RFQTicketFields-CollapsePanel-TicketAudit'} title={Locale.translate(Translation.TC_TICKET_AUDIT)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_CREATION_TIMESTAMP)}
                readOnly={true}
                onUpdate={noop}
                value={Locale.formatTimestamp(negotiation.createdAt, userPreference.dateFormat)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_LAST_UPDATED_BY)}
                readOnly={true}
                onUpdate={noop}
                value={negotiation.lastUpdatedBy}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_LAST_UPDATED_AT)}
                readOnly={true}
                onUpdate={noop}
                value={Locale.formatTimestamp(negotiation.lastUpdatedAt, userPreference.dateFormat)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_LAST_INITIATOR)}
                readOnly={true}
                onUpdate={noop}
                value={negotiation.lastInitiatorId}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_LAST_RESPONDER)}
                readOnly={true}
                onUpdate={noop}
                value={negotiation.lastResponderId}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_ORIGINATOR)}
                readOnly={true}
                onUpdate={noop}
                value={negotiation.createdBy}
              />
            </DialogField>
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel
        dataTestId={'RFQTicketFields-CollapsePanel-TikcetInternationalBooking'}
        title={Locale.translate(Translation.TC_TICKET_INTERNAL_BOOKING)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_BOOK)} readOnly={true} onUpdate={noop} value={book} />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_STRATEGY)} readOnly={true} onUpdate={noop} value={strategy} />
            </DialogField>
          </DialogRow>
          {ownOrder && (
            <DialogRow>
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_PRIME_BROKER)}
                  readOnly={true}
                  onUpdate={noop}
                  value={isNegotiationHistory(negotiation) ? null : negotiation.initiatorPrimeBrokerName}
                />
              </DialogField>
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_BUNDLE)}
                  readOnly={true}
                  onUpdate={noop}
                  value={negotiation.bundle}
                />
              </DialogField>
              <DialogField />
              <DialogField />
            </DialogRow>
          )}
        </CollapseContentWrapper>
      </CollapsePanel>
    </TicketFields>
  )
}
